import { Alert, Button, Divider, Grid, LinearProgress, Paper, Typography, useTheme } from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import ImsPaperHead from "../../../atoms/ImsPaperHead";
import { AISegmentGroup } from "../../../../store/models/segments/AISegmentGroup";
import { Api } from "../../../../api/Api";
import {
  thunkCreateErrorNotification,
  thunkCreateSuccessNotification,
} from "../../../../store/actions/NotificationActions";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UrlPathSegment } from "../../../../api/url";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { connect, ConnectedProps } from "react-redux";
import KnistrConfirmDialog from "../../../atoms/KnistrConfirmDialog";

interface Step2Props {
  selectedModel: AISegmentGroup;
  onBack: () => void;
}

const contentConfig = {
  clv: {
    activate: Api.activateCustomerLifetimeValue,
    deactivate: Api.deactivateCustomerLifetimeValue,
    description:"segmentBuilder.ai.step2.clvDescription",
    fullDescriptionKey: "segmentBuilder.ai.groups.customerLifetimeValue.fullDescription",
    segmentDescriptionKey: "segmentBuilder.ai.groups.customerLifetimeValue.segmentDescription",
    useCasesKey: "segmentBuilder.ai.groups.customerLifetimeValue.useCases",
    labelKey: "segmentBuilder.ai.groups.customerLifetimeValue.label",
    successMessageKey: "segmentBuilder.messages.clvActivated",
  },
  ci: {
    activate: Api.activateCustomerRetentionModel,
    deactivate: Api.deactivateRetentionModel,
    description:"segmentBuilder.ai.step2.churnDescription",
    fullDescriptionKey: "segmentBuilder.ai.groups.customerRetention.fullDescription",
    segmentDescriptionKey: "segmentBuilder.ai.groups.customerRetention.segmentDescription",
    useCasesKey: "segmentBuilder.ai.groups.customerRetention.useCases",
    labelKey: "segmentBuilder.ai.groups.customerRetention.label",
    successMessageKey: "segmentBuilder.messages.ciActivated",
  },
};

const Step2 = ({
  selectedModel,
  onBack,
  thunkCreateSuccessNotification,
  thunkCreateErrorNotification,
}: Step2Props & ConnectedProps<typeof connector>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const navigate = useNavigate();

  const goToListView = useCallback(() => {
    navigate(UrlPathSegment);
  }, [navigate]);

  const showErrorNotifications = useCallback((
    action: "activate" | "deactivate",
    error: unknown
  ) => {
    const key =
      action === "activate"
        ? "segmentBuilder.messages.errorActivateModel"
        : "segmentBuilder.messages.errorDeactivatingModel";
    if (Array.isArray(error)) {
      error.forEach((e) => {
        const errorMessage =  e?.msg;
        thunkCreateErrorNotification(t(key), errorMessage);
      });
    } else {
      thunkCreateErrorNotification(t(key), error);
    }
  }, [t, thunkCreateErrorNotification]);

  const handleModelAction = useCallback(
    async (action: "activate" | "deactivate") => {
      setProcessing(true);
      const content = contentConfig[selectedModel.modelType];
      try {
        await content[action]();
        thunkCreateSuccessNotification(
          t(action === "activate" ? content.successMessageKey : "segmentBuilder.messages.modelDeactivated")
        );
        goToListView();
      } catch (error) {
        showErrorNotifications(action, error);
      } finally {
        setProcessing(false);
      }
    },
    [selectedModel, goToListView, t, showErrorNotifications, thunkCreateSuccessNotification]
  );

  const handleToggle = () => {
    if (!selectedModel) return;
    if (selectedModel.status === "enabled") {
      setConfirmVisible(true);
    } else if (selectedModel.status === "disabled") {
      handleModelAction("activate").catch(() => {});
    }
  };

  const confirmDialog = useMemo(
    () =>
      confirmVisible && (
        <KnistrConfirmDialog
          title={t("segmentBuilder.ai.confirmDeleteTitle")}
          description={t("segmentBuilder.ai.confirmDeleteDescription")}
          cancelButton={t("common.cancel")}
          confirmButton={t("common.deactivate")}
          onConfirm={() => {
            setConfirmVisible(false);
            handleModelAction("deactivate").catch(() => {});
          }}
          setConfirmVisible={setConfirmVisible}
        />
      ),
    [confirmVisible, t, handleModelAction]
  );

  const content = contentConfig[selectedModel.modelType];

  return (
    <>
      {processing && <LinearProgress />}
      {confirmDialog}
      <Paper style={{ padding: theme.spacing(3), marginTop: theme.spacing(6) }}>
        <ImsPaperHead text={t("segmentBuilder.ai.step2.title")} />
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <h3>{t(content.description)}</h3>
            <Typography variant="body1" style={{ marginBottom: theme.spacing(2) }}>
              <Trans>{t(content.fullDescriptionKey)}</Trans>
            </Typography>
            {selectedModel.status === "disabled" && (
              <Typography variant="body1" style={{ marginBottom: theme.spacing(2) }}>
                <Trans>
                  <Alert severity="info">{t("segmentBuilder.ai.tos.description")}</Alert>
                </Trans>
              </Typography>
            )}
            <Button
              color="primary"
              variant="contained"
              style={{ margin: theme.spacing(2, 0, 2, 0), minWidth: 150 }}
              disabled={!selectedModel}
              onClick={handleToggle}
              data-testid="status-toggle-button"
            >
              {selectedModel.status === "enabled" ? t("common.deactivate") : t("common.activate")}
            </Button>
            <Divider style={{ margin: theme.spacing(2, 0) }} />
            <h3>{t(content.labelKey)}:</h3>
            <Typography variant="body1" style={{ marginBottom: theme.spacing(2) }}>
              <Trans
                components={{
                  strong: <strong />,
                  table: (
                    <table style={{ borderCollapse: "collapse", width: "100%" }}>
                      <thead>
                        <tr>
                          <th scope="col" />
                        </tr>
                      </thead>
                      <tbody />
                    </table>
                  ),
                  thead: <thead />,
                  tbody: <tbody />,
                  tr: <tr />,
                  th: <th scope="col" style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }} />,
                  td: <td style={{ border: "1px solid #ddd", padding: "8px" }} />,
                }}
              >
                {t(content.segmentDescriptionKey)}
              </Trans>
            </Typography>
            <Typography variant="body1" style={{ marginBottom: theme.spacing(2) }}>
              <Trans components={{ ul: <ul />, li: <li /> }}>{t(content.useCasesKey)}</Trans>
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          color="secondary"
          variant="contained"
          style={{ margin: theme.spacing(2, 0, 2, 0), minWidth: 150 }}
          onClick={onBack}
        >
          {t("common.back")}
        </Button>
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkCreateSuccessNotification,
      thunkCreateErrorNotification,
    },
    dispatch
  );

const connector = connect(null, mapDispatchToProps);

export default connector(Step2);
